import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../api';
import NotesEditor from '../components/NotesEditor';

const hakedisToForm = (h) => ({
  customer_id: h.customer_id || '',
  project_name: h.project_name || '',
  location_id: h.location_id || '',
  status: h.status || 'draft',
  hakedis_date: h.hakedis_date,
  notes: h.notes || '',
  vat_rate: h.vat_rate,
  items: h.items.map(item => ({
    ...item,
    labor_price: item.labor_price || 0,
  }))
});

const CURRENCY_SYMBOLS = { TRY: '₺', USD: '$', EUR: '€' };
// Döviz malzeme kutuları ve satır renkleri; işçilik her zaman TL
const FX_CURRENCIES = [
  { code: 'USD', quoteField: 'subtotal_usd', box: 'bg-blue-50 border-blue-200', title: 'text-blue-600', value: 'text-blue-700', total: 'text-blue-800', border: 'border-blue-200' },
  { code: 'EUR', quoteField: 'subtotal_eur', box: 'bg-purple-50 border-purple-200', title: 'text-purple-600', value: 'text-purple-700', total: 'text-purple-800', border: 'border-purple-200' },
];
const ROW_BG = { USD: 'bg-blue-50', EUR: 'bg-purple-50' };
const SELECT_STYLE = {
  USD: 'bg-blue-100 text-blue-700 border-blue-300',
  EUR: 'bg-purple-100 text-purple-700 border-purple-300',
};

export default function HakedisForm() {
  // /hakedis/:quoteId → teklifli, /hakedisler/yeni → teklifsiz, /hakedisler/:hakedisId → mevcut hakediş
  const { quoteId, hakedisId } = useParams();
  const navigate = useNavigate();
  const backPath = quoteId ? '/quotes' : '/hakedisler';

  const [quote, setQuote] = useState(null);
  const [existingHakedis, setExistingHakedis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);

  // Teklifsiz hakediş için ürün/müşteri seçimi
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [customerPrices, setCustomerPrices] = useState([]);
  const [onlyCustomerPrices, setOnlyCustomerPrices] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');

  const [form, setForm] = useState({
    customer_id: '',
    project_name: '',
    location_id: '',
    status: 'draft',
    hakedis_date: new Date().toISOString().split('T')[0],
    notes: '',
    vat_rate: 20,
    items: []
  });

  useEffect(() => {
    api.get('/api/locations/').then(res => setLocations(res.data.filter(l => l.is_active !== false))).catch(() => setLocations([]));
  }, []);

  const loadCustomerPrices = async (customerId) => {
    if (!customerId) return setCustomerPrices([]);
    try {
      const res = await api.get(`/api/customer-prices/customer/${customerId}`);
      setCustomerPrices(res.data);
    } catch {
      setCustomerPrices([]);
    }
  };

  useEffect(() => {
    if (quoteId) return;
    const fetchStandalone = async () => {
      try {
        const [c, p] = await Promise.all([api.get('/api/customers/'), api.get('/api/products/')]);
        setCustomers(c.data);
        setProducts(p.data);
        api.get('/api/hakedis-templates/').then(t => setTemplates(t.data)).catch(() => setTemplates([]));
        if (hakedisId) {
          const hRes = await api.get(`/api/hakedis/${hakedisId}`);
          setExistingHakedis(hRes.data);
          setForm(hakedisToForm(hRes.data));
          if (hRes.data.quote_id) {
            const qRes = await api.get(`/api/quotes/${hRes.data.quote_id}`);
            setQuote(qRes.data);
          } else {
            loadCustomerPrices(hRes.data.customer_id);
          }
        }
      } catch (err) {
        alert('Hata: ' + JSON.stringify(err.response?.data?.detail || 'Yüklenemedi'));
        navigate('/hakedisler');
      } finally {
        setPageLoading(false);
      }
    };
    fetchStandalone();
  }, [quoteId, hakedisId, navigate]);

  useEffect(() => {
    if (!quoteId) return;
    const fetchData = async () => {
      try {
        const qRes = await api.get(`/api/quotes/${quoteId}`);
        setQuote(qRes.data);

        // Mevcut hakediş var mı?
        try {
          const hRes = await api.get(`/api/hakedis/quote/${quoteId}`);
          setExistingHakedis(hRes.data);
          setForm(hakedisToForm(hRes.data));
        } catch {
          // Hakediş yok — teklif kalemlerini kopyala
          const items = qRes.data.items.map(item => ({
            quote_item_id: item.id,
            product_id: item.product_id || null,
            description: item.description,
            quantity: item.quantity,
            unit: item.unit,
            unit_sale_price: item.unit_sale_price,
            item_currency: item.item_currency || 'TRY',
            labor_price: item.labor_price || 0,
            sort_order: item.sort_order,
            section_title: item.section_title
          }));
          setForm(f => ({
            ...f,
            vat_rate: qRes.data.vat_rate,
            items
          }));
        }
      } finally {
        setPageLoading(false);
      }
    };
    fetchData();
  }, [quoteId]);

  const isStandalone = !quote;

  const handleCustomerChange = (customerId) => {
    setForm({ ...form, customer_id: customerId });
    setOnlyCustomerPrices(false);
    setCustomerPrices([]);
    loadCustomerPrices(customerId);
  };

  // Müşteriye özel fiyatlı ürünler üstte; filtre açıksa yalnızca onlar
  const customerPriceMap = new Map(customerPrices.map(cp => [cp.product_id, cp]));
  const productOptions = products
    .filter(p => !onlyCustomerPrices || customerPriceMap.has(p.id))
    .sort((a, b) => (customerPriceMap.has(b.id) ? 1 : 0) - (customerPriceMap.has(a.id) ? 1 : 0));

  // Ürün seçilince fiyatlar: müşteriye özel fiyat varsa o, yoksa ürünün varsayılanı
  const productToItem = (product) => {
    const cp = customerPriceMap.get(product.id);
    return {
      product_id: product.id,
      description: product.name,
      unit: product.unit,
      unit_sale_price: cp ? cp.sale_price : product.default_sale_price,
      item_currency: product.currency || 'TRY',
      labor_price: cp?.labor_price ?? (product.default_labor_price || 0),
    };
  };

  const selectProduct = (index, productId) => {
    const product = products.find(p => p.id === parseInt(productId));
    if (!product) return;
    const items = [...form.items];
    items[index] = { ...items[index], ...productToItem(product) };
    setForm({ ...form, items });
  };

  const applyTemplate = () => {
    const template = templates.find(t => t.id === parseInt(selectedTemplateId));
    if (!template) return;
    let replace = false;
    if (form.items.length > 0) {
      replace = window.confirm('Mevcut kalemler silinip yerine şablon kalemleri konsun mu?\n\nTamam: Değiştir — İptal: Sona ekle');
    }
    const skipped = [];
    const newItems = template.items.flatMap(ti => {
      if (ti.section_title) {
        return [{
          quote_item_id: null, product_id: null, description: ti.description,
          quantity: 0, unit: '-', unit_sale_price: 0,
          item_currency: 'TRY', labor_price: 0, section_title: true
        }];
      }
      const product = products.find(p => p.id === ti.product_id);
      if (!product) {
        skipped.push(ti.product_name || `#${ti.product_id}`);
        return [];
      }
      return [{
        quote_item_id: null, section_title: false,
        ...productToItem(product),
        quantity: ti.quantity,
        unit: ti.unit || product.unit,
      }];
    });
    setForm({ ...form, items: replace ? newItems : [...form.items, ...newItems] });
    setSelectedTemplateId('');
    if (skipped.length) alert('Bulunamayan ürünler atlandı: ' + skipped.join(', '));
  };

  const addItem = () => {
    setForm({
      ...form,
      items: [...form.items, {
        quote_item_id: null, product_id: null, description: '', quantity: 1,
        unit: 'Adet', unit_sale_price: 0,
        item_currency: 'TRY', labor_price: 0,
        section_title: false
      }]
    });
  };

  // Serbest kalem: Ürünler'e bağlanmaz/eklenmez, açıklama ve fiyat elle girilir
  const addFreeItem = () => {
    setForm({
      ...form,
      items: [...form.items, {
        quote_item_id: null, product_id: null, description: '', quantity: 1,
        unit: 'Adet', unit_sale_price: 0,
        item_currency: 'TRY', labor_price: 0,
        section_title: false, free_item: true
      }]
    });
  };

  const addSection = () => {
    setForm({
      ...form,
      items: [...form.items, {
        quote_item_id: null, description: 'YENİ BÖLÜM BAŞLIĞI',
        quantity: 0, unit: '-', unit_sale_price: 0,
        item_currency: 'TRY', labor_price: 0,
        section_title: true
      }]
    });
  };

  const removeItem = (index) => {
    setForm({ ...form, items: form.items.filter((_, i) => i !== index) });
  };

  const updateItem = (index, field, value) => {
    const items = [...form.items];
    items[index] = { ...items[index], [field]: value };
    setForm({ ...form, items });
  };

  const moveItem = (index, direction) => {
    const items = [...form.items];
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= items.length) return;
    [items[index], items[newIndex]] = [items[newIndex], items[index]];
    setForm({ ...form, items });
  };

  const realItems = form.items.filter(i => !i.section_title);

  // Bir dövizin malzeme toplamı (USD veya EUR)
  const calcMalzeme = (cur) => realItems
    .filter(i => i.item_currency === cur)
    .reduce((s, i) => s + (parseFloat(i.unit_sale_price) || 0) * (parseFloat(i.quantity) || 0), 0);

  // TRY: TL malzeme + tüm işçilik
  const calcTRY = () => realItems
    .filter(i => (i.item_currency || 'TRY') === 'TRY')
    .reduce((s, i) => s + ((parseFloat(i.unit_sale_price) || 0)) * (parseFloat(i.quantity) || 0), 0)
    + realItems.reduce((s, i) => s + (parseFloat(i.labor_price) || 0) * (parseFloat(i.quantity) || 0), 0);

  const usedFx = FX_CURRENCIES.filter(fx => realItems.some(i => i.item_currency === fx.code));
  const hasTRY = realItems.some(i => (i.item_currency || 'TRY') === 'TRY') || realItems.some(i => (parseFloat(i.labor_price) || 0) > 0);

  // Teklif karşılaştırma
  const quoteTRY = quote ? (quote.subtotal_try || 0) : 0;
  const diffTRY = calcTRY() - quoteTRY;
  const fxComparisons = FX_CURRENCIES
    .map(fx => {
      const q = quote ? (quote[fx.quoteField] || 0) : 0;
      const h = calcMalzeme(fx.code);
      return { ...fx, q, h, diff: h - q };
    })
    .filter(c => c.q > 0 || c.h > 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        ...(isStandalone
          ? { customer_id: parseInt(form.customer_id), project_name: form.project_name || null }
          : { quote_id: quote.id }),
        location_id: form.location_id ? parseInt(form.location_id) : null,
        status: form.status,
        hakedis_date: form.hakedis_date,
        notes: (form.notes || '').split('\n').map(n => n.trim()).filter(Boolean).join('\n') || null,
        vat_rate: form.vat_rate,
        items: form.items.map(item => ({
          ...item,
          quote_item_id: item.quote_item_id || null,
          product_id: item.free_item ? null : (item.product_id || null),
          free_item: !!item.free_item,
          quantity: parseFloat(item.quantity) || 0,
          unit_sale_price: parseFloat(item.unit_sale_price) || 0,
          labor_price: parseFloat(item.labor_price) || 0,
          item_currency: item.item_currency || 'TRY',
        }))
      };

      if (existingHakedis) {
        await api.put(`/api/hakedis/${existingHakedis.id}`, payload);
      } else {
        await api.post('/api/hakedis/', payload);
      }
      navigate(backPath);
    } catch (err) {
      alert('Hata: ' + JSON.stringify(err.response?.data?.detail || 'Bilinmeyen hata'));
    } finally {
      setLoading(false);
    }
  };

  if (pageLoading) return <div className="text-center py-20 text-gray-400">Yükleniyor...</div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">
            {existingHakedis ? `Hakediş Düzenle — ${existingHakedis.hakedis_number}` : isStandalone ? 'Yeni Hakediş (Teklifsiz)' : 'Yeni Hakediş'}
          </h2>
          {quote ? (
            <p className="text-sm text-gray-500 mt-1">
              Teklif: <span className="font-medium text-blue-600">{quote.quote_number}</span>
              {quote.project_name && <span> — {quote.project_name}</span>}
            </p>
          ) : (
            <p className="text-sm text-gray-500 mt-1">Teklife bağlı değil — kullanılan ürün ve işçiliklere göre kalemleri girin</p>
          )}
        </div>
        <button onClick={() => navigate(backPath)} className="text-gray-500 hover:text-gray-700">← Geri</button>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Üst bilgiler */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h3 className="font-semibold text-gray-700 mb-4">Hakediş Bilgileri</h3>
          <div className="grid grid-cols-3 gap-4">
            {isStandalone && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Müşteri *</label>
                  <select required value={form.customer_id} onChange={e => handleCustomerChange(e.target.value)} className="w-full border rounded px-3 py-2">
                    <option value="">Seçiniz</option>
                    {customers.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Proje / İş Adı</label>
                  <input value={form.project_name} onChange={e => setForm({...form, project_name: e.target.value})} className="w-full border rounded px-3 py-2" />
                </div>
              </>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Hakediş Tarihi *</label>
              <input type="date" required value={form.hakedis_date}
                onChange={e => setForm({...form, hakedis_date: e.target.value})}
                className="w-full border rounded px-3 py-2" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">KDV Oranı %</label>
              <input type="number" value={form.vat_rate}
                onChange={e => setForm({...form, vat_rate: parseFloat(e.target.value)})}
                className="w-full border rounded px-3 py-2" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Depo {form.status === 'final' && '*'}</label>
              <select required={form.status === 'final'} value={form.location_id}
                onChange={e => setForm({...form, location_id: e.target.value})} className="w-full border rounded px-3 py-2">
                <option value="">Seçiniz</option>
                {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Durum</label>
              <select value={form.status} onChange={e => setForm({...form, status: e.target.value})}
                className={`w-full border rounded px-3 py-2 ${form.status === 'final' ? 'bg-green-50 text-green-700 font-medium' : ''}`}>
                <option value="draft">Taslak</option>
                <option value="final">Kesinleşti</option>
              </select>
              {form.status === 'final' && (
                <p className="text-xs text-gray-500 mt-1">Kaydedince ürün kalemleri depodan düşülür (serbest ve saat/gün birimli kalemler hariç)</p>
              )}
            </div>
            <div className="col-span-3">
              <NotesEditor kind="hakedis" value={form.notes} onChange={notes => setForm({ ...form, notes })} />
            </div>
          </div>
        </div>

        {/* Teklif vs Hakediş karşılaştırma */}
        {quote && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <h4 className="font-semibold text-blue-800 mb-3 text-sm">📊 Teklif vs Hakediş Karşılaştırması</h4>
            <div className="grid grid-cols-2 gap-4">
              {fxComparisons.map(c => {
                const sym = CURRENCY_SYMBOLS[c.code];
                return (
                  <div key={c.code} className="bg-white rounded p-3 border border-blue-100">
                    <div className="text-xs text-gray-500 mb-2 font-medium">{c.code} Malzeme</div>
                    <div className="flex justify-between text-sm"><span className="text-gray-600">Teklif:</span><span className="font-medium">{sym}{c.q.toFixed(2)}</span></div>
                    <div className="flex justify-between text-sm"><span className="text-gray-600">Hakediş:</span><span className="font-medium">{sym}{c.h.toFixed(2)}</span></div>
                    <div className={`flex justify-between text-sm font-bold mt-1 pt-1 border-t ${c.diff > 0 ? 'text-red-600' : c.diff < 0 ? 'text-green-600' : 'text-gray-500'}`}>
                      <span>Fark:</span>
                      <span>{c.diff > 0 ? '+' : ''}{sym}{c.diff.toFixed(2)}</span>
                    </div>
                  </div>
                );
              })}
              {quoteTRY > 0 || calcTRY() > 0 ? (
                <div className="bg-white rounded p-3 border border-blue-100">
                  <div className="text-xs text-gray-500 mb-2 font-medium">TRY (Malzeme + İşçilik)</div>
                  <div className="flex justify-between text-sm"><span className="text-gray-600">Teklif:</span><span className="font-medium">₺{quoteTRY.toFixed(2)}</span></div>
                  <div className="flex justify-between text-sm"><span className="text-gray-600">Hakediş:</span><span className="font-medium">₺{calcTRY().toFixed(2)}</span></div>
                  <div className={`flex justify-between text-sm font-bold mt-1 pt-1 border-t ${diffTRY > 0 ? 'text-red-600' : diffTRY < 0 ? 'text-green-600' : 'text-gray-500'}`}>
                    <span>Fark:</span>
                    <span>{diffTRY > 0 ? '+' : ''}₺{diffTRY.toFixed(2)}</span>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        )}

        {/* Kalemler */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-semibold text-gray-700">Gerçekleşen Kalemler</h3>
            <div className="flex gap-2 items-center">
              {isStandalone && (
                <label className={`flex items-center gap-1 text-sm mr-2 ${customerPrices.length ? 'text-purple-700' : 'text-gray-400'}`}>
                  <input type="checkbox" checked={onlyCustomerPrices} disabled={!customerPrices.length}
                    onChange={e => setOnlyCustomerPrices(e.target.checked)} />
                  Sadece müşteriye özel fiyatlılar ({customerPrices.length})
                </label>
              )}
              {isStandalone && templates.length > 0 && (
                <div className="flex items-center gap-1 mr-2"
                  title={form.customer_id ? '' : 'Müşteriye özel fiyatların gelmesi için önce müşteri seçin'}>
                  <select value={selectedTemplateId} onChange={e => setSelectedTemplateId(e.target.value)}
                    disabled={!form.customer_id} className="border rounded px-2 py-1 text-sm disabled:opacity-50">
                    <option value="">Şablon seçin...</option>
                    {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                  <button type="button" onClick={applyTemplate} disabled={!form.customer_id || !selectedTemplateId}
                    className="bg-purple-600 text-white px-3 py-1 rounded hover:bg-purple-700 text-sm disabled:opacity-50">
                    Şablondan Ekle
                  </button>
                </div>
              )}
              <button type="button" onClick={addSection} className="bg-gray-600 text-white px-3 py-1 rounded hover:bg-gray-700 text-sm">+ Bölüm Başlığı</button>
              <button type="button" onClick={addItem} className="bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700 text-sm">+ Kalem Ekle</button>
              <button type="button" onClick={addFreeItem} title="Ürünler tablosundan bağımsız; açıklama ve fiyat elle girilir"
                className="bg-amber-600 text-white px-3 py-1 rounded hover:bg-amber-700 text-sm">+ Serbest Kalem</button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-2 py-2 w-10"></th>
                  {isStandalone && <th className="text-left px-2 py-2 text-gray-500">Ürün</th>}
                  <th className="text-left px-2 py-2 text-gray-500">Açıklama *</th>
                  <th className="text-left px-2 py-2 text-gray-500">Miktar</th>
                  <th className="text-left px-2 py-2 text-gray-500">Birim</th>
                  <th className="text-left px-2 py-2 text-gray-500">Döviz</th>
                  <th className="text-right px-2 py-2 text-gray-500">Malzeme Br.</th>
                  <th className="text-right px-2 py-2 text-gray-500">İşçilik Br. ₺</th>
                  <th className="text-right px-2 py-2 text-gray-500">Malzeme Top.</th>
                  <th className="text-right px-2 py-2 text-gray-500">İşçilik Top.</th>
                  <th className="px-2 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {form.items.map((item, i) => (
                  item.section_title ? (
                    <tr key={i} className="bg-gray-700">
                      <td className="px-2 py-2">
                        <div className="flex flex-col gap-0.5">
                          <button type="button" onClick={() => moveItem(i, -1)} className="text-gray-300 hover:text-white text-xs">▲</button>
                          <button type="button" onClick={() => moveItem(i, 1)} className="text-gray-300 hover:text-white text-xs">▼</button>
                        </div>
                      </td>
                      <td colSpan={isStandalone ? 8 : 7} className="px-3 py-2">
                        <input value={item.description} onChange={e => updateItem(i, 'description', e.target.value)}
                          className="w-full bg-transparent text-white font-bold uppercase tracking-wider border-b border-gray-500 focus:outline-none" />
                      </td>
                      <td></td>
                      <td className="px-2 py-2">
                        <button type="button" onClick={() => removeItem(i)} className="text-red-300 hover:text-red-100 font-bold text-lg">×</button>
                      </td>
                    </tr>
                  ) : (
                    <tr key={i} className={`align-top hover:bg-gray-50 ${item.quote_item_id || item.free_item || isStandalone ? '' : 'bg-yellow-50'} ${ROW_BG[item.item_currency] || ''}`}>
                      <td className="px-2 py-2">
                        <div className="flex flex-col gap-0.5">
                          <button type="button" onClick={() => moveItem(i, -1)} className="text-gray-400 hover:text-gray-600 text-xs">▲</button>
                          <button type="button" onClick={() => moveItem(i, 1)} className="text-gray-400 hover:text-gray-600 text-xs">▼</button>
                        </div>
                      </td>
                      {isStandalone && (
                        <td className="px-2 py-2">
                          {item.free_item ? (
                            <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded font-medium">Serbest</span>
                          ) : (
                          <select value={item.product_id || ''} onChange={e => selectProduct(i, e.target.value)} className="border rounded px-1 py-1 text-xs w-28">
                            <option value="">-- Seç --</option>
                            {productOptions.map(p => {
                              const cp = customerPriceMap.get(p.id);
                              return (
                                <option key={p.id} value={p.id}>
                                  {cp ? `★ ${p.name} (${cp.sale_price} ${cp.currency})` : p.name}
                                </option>
                              );
                            })}
                          </select>
                          )}
                        </td>
                      )}
                      <td className="px-2 py-2">
                        <div className="flex items-center gap-1">
                          {!item.quote_item_id && !isStandalone && !item.free_item && <span className="text-xs bg-yellow-200 text-yellow-800 px-1 rounded">YENİ</span>}
                          {item.free_item && !isStandalone && <span className="text-xs bg-amber-100 text-amber-700 px-1 rounded">SERBEST</span>}
                          <input required value={item.description} onChange={e => updateItem(i, 'description', e.target.value)} className="border rounded px-2 py-1 w-48" />
                        </div>
                        {isStandalone && !item.product_id && !item.free_item && item.description?.trim() && (
                          <div className="text-xs text-amber-600 mt-0.5">Kaydedince Ürünler'e eklenecek</div>
                        )}
                      </td>
                      <td className="px-2 py-2">
                        <input type="number" step="0.5" value={item.quantity} onChange={e => updateItem(i, 'quantity', parseFloat(e.target.value))} className="border rounded px-2 py-1 w-16" />
                      </td>
                      <td className="px-2 py-2">
                        <select value={item.unit} onChange={e => updateItem(i, 'unit', e.target.value)} className="border rounded px-1 py-1 text-xs">
                          {['Adet', 'Metre', 'Saat', 'Gün', 'Adam/Gün', 'Paket', 'Set', 'Kutu'].map(u => <option key={u}>{u}</option>)}
                        </select>
                      </td>
                      <td className="px-2 py-2">
                        <select value={item.item_currency} onChange={e => updateItem(i, 'item_currency', e.target.value)}
                          className={`border rounded px-1 py-1 text-xs font-bold ${SELECT_STYLE[item.item_currency] || 'bg-gray-100 text-gray-700'}`}>
                          <option value="TRY">₺ TRY</option>
                          <option value="USD">$ USD</option>
                          <option value="EUR">€ EUR</option>
                        </select>
                      </td>
                      <td className="px-2 py-2">
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-gray-400">{CURRENCY_SYMBOLS[item.item_currency] || '₺'}</span>
                          <input type="number" step="0.01" value={item.unit_sale_price} onChange={e => updateItem(i, 'unit_sale_price', parseFloat(e.target.value))} className="border rounded px-2 py-1 w-24 text-right" />
                        </div>
                      </td>
                      <td className="px-2 py-2">
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-gray-400">₺</span>
                          <input type="number" step="0.01" value={item.labor_price} onChange={e => updateItem(i, 'labor_price', parseFloat(e.target.value))} className="border rounded px-2 py-1 w-24 text-right" placeholder="0" />
                        </div>
                      </td>
                      <td className="px-2 py-2 text-right font-medium whitespace-nowrap">
                        {(parseFloat(item.unit_sale_price) || 0) > 0
                          ? <span className={FX_CURRENCIES.find(fx => fx.code === item.item_currency)?.value || ''}>
                              {CURRENCY_SYMBOLS[item.item_currency] || '₺'}{((parseFloat(item.unit_sale_price)||0)*(parseFloat(item.quantity)||0)).toFixed(2)}
                            </span>
                          : <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-2 py-2 text-right font-medium whitespace-nowrap">
                        {(parseFloat(item.labor_price) || 0) > 0
                          ? <span className="text-green-700">₺{((parseFloat(item.labor_price)||0)*(parseFloat(item.quantity)||0)).toFixed(2)}</span>
                          : <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-2 py-2">
                        <button type="button" onClick={() => removeItem(i)} className="text-red-500 hover:text-red-700 font-bold text-lg">×</button>
                      </td>
                    </tr>
                  )
                ))}
              </tbody>
            </table>
            {form.items.length === 0 && (
              <div className="text-center py-8 text-gray-400">Kalem eklemek için yukarıdaki butonları kullanın</div>
            )}
          </div>
        </div>

        {/* Toplam özet */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex justify-end">
            <div className="w-80">
              {usedFx.map(fx => {
                const sym = CURRENCY_SYMBOLS[fx.code];
                const sub = calcMalzeme(fx.code);
                return (
                  <div key={fx.code} className={`border rounded p-3 mb-3 ${fx.box}`}>
                    <div className={`text-xs font-bold uppercase mb-2 ${fx.title}`}>{fx.code} Malzeme</div>
                    <div className="flex justify-between py-1 text-sm"><span className="text-gray-600">Ara Toplam</span><span className={`font-medium ${fx.value}`}>{sym}{sub.toFixed(2)}</span></div>
                    <div className="flex justify-between py-1 text-sm"><span className="text-gray-600">KDV (%{form.vat_rate})</span><span className={fx.value}>{sym}{(sub * form.vat_rate / 100).toFixed(2)}</span></div>
                    <div className={`flex justify-between py-1 text-sm font-bold border-t mt-1 pt-1 ${fx.border}`}><span>{fx.code} Toplam</span><span className={fx.total}>{sym}{(sub * (1 + form.vat_rate / 100)).toFixed(2)}</span></div>
                  </div>
                );
              })}
              {hasTRY && (
                <div className="bg-gray-50 border border-gray-200 rounded p-3 mb-3">
                  <div className="text-xs font-bold text-gray-600 uppercase mb-2">TRY (Malzeme + İşçilik)</div>
                  <div className="flex justify-between py-1 text-sm"><span className="text-gray-600">Ara Toplam</span><span className="font-medium">₺{calcTRY().toFixed(2)}</span></div>
                  <div className="flex justify-between py-1 text-sm"><span className="text-gray-600">KDV (%{form.vat_rate})</span><span>₺{(calcTRY() * form.vat_rate / 100).toFixed(2)}</span></div>
                  <div className="flex justify-between py-1 text-sm font-bold border-t border-gray-200 mt-1 pt-1"><span>TRY Toplam</span><span>₺{(calcTRY() * (1 + form.vat_rate / 100)).toFixed(2)}</span></div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex gap-3">
          <button type="submit" disabled={loading} className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50">
            {loading ? 'Kaydediliyor...' : existingHakedis ? 'Güncelle' : 'Kaydet'}
          </button>
          <button type="button" onClick={() => navigate(backPath)} className="bg-gray-100 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-200 font-medium">
            İptal
          </button>
        </div>
      </form>
    </div>
  );
}
